/**
 * Plan Command Autocomplete Component
 * Dropdown for document selection when typing @plan command
 * Shows open documents first, then allows searching for others
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { searchApi, projectsApi } from '../services/api';
import { logger } from '../utils/logger';
import { useAutocompleteKeyboard } from '../hooks/useAutocompleteKeyboard';

interface SearchResult {
  id: string;
  documentNumber?: string;
  projectId?: string;
  projectCode?: string;
  title: string;
  type: 'project' | 'company' | 'standard' | 'prior-project';
  score: number;
  snippet?: string;
  canEdit?: boolean;
  metadata?: Record<string, unknown>;
}

interface OpenedDocument {
  id: string;
  tabId: number;
  documentNumber?: string;
  projectId?: string;
  title: string;
  type: string;
  canEdit: boolean;
}

interface PlanCommandAutocompleteProps {
  query: string;
  onSelect: (result: SearchResult) => void;
  onClose: () => void;
  position?: { top: number; left: number };
  currentProjectId?: string;
  openedDocuments: OpenedDocument[];
}

export default function PlanCommandAutocomplete({
  query,
  onSelect,
  onClose,
  position,
  currentProjectId,
  openedDocuments
}: PlanCommandAutocompleteProps) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [projectDocs, setProjectDocs] = useState<SearchResult[]>([]);
  const [recentDocs, setRecentDocs] = useState<SearchResult[]>([]);

  // Convert opened documents to SearchResult format (excluding plan tabs)
  // Include docs with documentNumber OR id (some docs may not have documentNumber set)
  const openDocResults: SearchResult[] = openedDocuments
    .filter(doc => doc.type !== 'plan' && (doc.documentNumber || doc.id))
    .map(doc => ({
      id: doc.documentNumber || doc.id, // Prefer documentNumber, fall back to id
      documentNumber: doc.documentNumber,
      projectId: doc.projectId,
      title: doc.title,
      type: doc.type as 'project' | 'company' | 'standard' | 'prior-project',
      score: 2.0, // Higher score for open docs
      canEdit: doc.canEdit,
    }));

  // Debug logging
  logger.debug('[PlanAutocomplete] openedDocuments:', openedDocuments.length, openedDocuments.map(d => ({ id: d.id, docNum: d.documentNumber, type: d.type })));
  logger.debug('[PlanAutocomplete] openDocResults:', openDocResults.length);
  logger.debug('[PlanAutocomplete] currentProjectId:', currentProjectId);

  // Load recent documents from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('aeroRecentDocuments');
      if (saved) {
        const recent = JSON.parse(saved);
        // Filter out already open docs and standards (can't edit)
        const openDocIds = new Set(openDocResults.map(d => d.id));
        const openDocNumbers = new Set(openDocResults.map(d => d.documentNumber).filter(Boolean));
        
        // Deduplicate by documentNumber (same doc can appear with different titles/ids)
        const seenDocNumbers = new Set<string>();
        const filtered = recent
          .filter((r: SearchResult) => {
            // Skip if already open (by id or documentNumber)
            if (openDocIds.has(r.id) || (r.documentNumber && openDocNumbers.has(r.documentNumber))) {
              return false;
            }
            // Skip standards (can't edit)
            if (r.type === 'standard') {
              return false;
            }
            // Deduplicate by documentNumber
            const docNum = r.documentNumber || r.id;
            if (seenDocNumbers.has(docNum)) {
              return false;
            }
            seenDocNumbers.add(docNum);
            return true;
          })
          .slice(0, 8);
        setRecentDocs(filtered);
      }
    } catch (error) {
      logger.error('Failed to load recent documents:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only re-run when open doc count changes, not on openDocResults identity change
  }, [openDocResults.length]);

  // Load current project documents
  useEffect(() => {
    if (!currentProjectId) {
      setProjectDocs([]);
      return;
    }

    const loadProjectDocs = async () => {
      try {
        const docs = await projectsApi.getDocuments(currentProjectId);
        // Convert to SearchResult format, excluding already open docs and recent docs
        const openDocNumbers = new Set(openDocResults.map(d => d.documentNumber));
        const recentDocNumbers = new Set(recentDocs.map(d => d.documentNumber));
        interface ProjectDoc {
          documentNumber: string;
          projectCode?: string;
          projectSpecificTitle?: string;
          documentTitle?: string;
          status?: string;
          currentRevision?: string;
        }
        const projectResults: SearchResult[] = docs
          .filter((doc: ProjectDoc) => !openDocNumbers.has(doc.documentNumber) && !recentDocNumbers.has(doc.documentNumber))
          .slice(0, 8)
          .map((doc: ProjectDoc) => ({
            id: doc.documentNumber, // Use documentNumber as canonical ID for deduplication
            documentNumber: doc.documentNumber,
            projectId: currentProjectId,
            projectCode: doc.projectCode,
            title: doc.projectSpecificTitle || doc.documentTitle || doc.documentNumber,
            type: 'project',
            score: 1.0,
            canEdit: true,
            metadata: {
              status: doc.status,
              revision: doc.currentRevision,
            }
          }));
        setProjectDocs(projectResults);
      } catch (error) {
        logger.error('Failed to load project documents:', error);
        setProjectDocs([]);
      }
    };

    loadProjectDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only re-run on count changes, not identity changes
  }, [currentProjectId, openDocResults.length, recentDocs.length]);

  // Debounced search
  useEffect(() => {
    if (!query || query.trim().length === 0) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await searchApi.searchAllDocuments(query.trim(), 10);
        // Filter out standards (can't edit) and already shown docs
        const openDocNumbers = new Set(openDocResults.map(d => d.documentNumber));
        const projectDocNumbers = new Set(projectDocs.map(d => d.documentNumber));
        const filtered = (response.results || []).filter((r: SearchResult) => 
          r.type !== 'standard' && 
          !openDocNumbers.has(r.documentNumber) &&
          !projectDocNumbers.has(r.documentNumber)
        );
        setResults(filtered);
      } catch (error) {
        logger.error('Search failed:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Debounced search only needs to re-run on query change
  }, [query]);

  // Combine all results for keyboard navigation
  const allResults = useMemo(() => [
    ...openDocResults,
    ...recentDocs,
    ...projectDocs,
    ...results
  ], [openDocResults, recentDocs, projectDocs, results]);

  const handleKeySelect = useCallback((index: number) => {
    if (allResults[index]) {
      onSelect(allResults[index]);
    }
  }, [allResults, onSelect]);

  const { selectedIndex } = useAutocompleteKeyboard({
    itemCount: allResults.length,
    onSelect: handleKeySelect,
    onClose,
    wrapAround: false,
    scrollIntoView: false,
    resetDeps: [query],
  });

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'project': return '📄';
      case 'company': return '🏢';
      case 'standard': return '📚';
      case 'prior-project': return '📁';
      default: return '📄';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'project': return 'Project';
      case 'company': return 'Company';
      case 'standard': return 'Standard';
      case 'prior-project': return 'Prior Project';
      default: return type;
    }
  };

  return (
    <div
      ref={containerRef}
      className="absolute z-50 w-[500px] max-h-[400px] overflow-y-auto bg-card border border-border rounded-lg shadow-xl"
      // eslint-disable-next-line no-restricted-syntax -- Dynamic position based on prop
      style={position ? { bottom: position.top, left: position.left } : { bottom: '100%', left: 0, marginBottom: '8px' }}
    >
      {/* Header */}
      <div className="sticky top-0 bg-info text-info-foreground px-4 py-2 border-b border-info">
        <div className="flex items-center gap-2">
          <span className="text-lg">📋</span>
          <span className="font-medium">Start Plan Mode</span>
        </div>
        <p className="text-xs text-info-foreground/70 mt-1">
          Select a document to create an AI-assisted editing plan
        </p>
      </div>

      {/* Open Documents Section */}
      {openDocResults.length > 0 && (
        <div className="border-b border-border">
          <div className="px-3 py-1.5 bg-muted text-xs font-medium text-muted-foreground uppercase tracking-wide">
            📂 Open Documents
          </div>
          {openDocResults.map((result, index) => (
            <div
              key={result.id}
              onClick={() => onSelect(result)}
              className={`px-4 py-2 cursor-pointer flex items-center gap-3 ${
                index === selectedIndex
                  ? 'bg-info-light'
                  : 'hover:bg-muted'
              }`}
            >
              <span className="text-lg">{getTypeIcon(result.type)}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground truncate">
                  {result.title}
                </div>
                <div className="text-xs text-muted-foreground">
                  {result.documentNumber} • {getTypeLabel(result.type)}
                </div>
              </div>
              <span className="text-xs px-2 py-0.5 bg-success-light text-success rounded">
                Open
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Recent Documents Section */}
      {recentDocs.length > 0 && (
        <div className="border-b border-border">
          <div className="px-3 py-1.5 bg-muted text-xs font-medium text-muted-foreground uppercase tracking-wide">
            🕐 Recent Documents
          </div>
          {recentDocs.map((result, index) => {
            const actualIndex = openDocResults.length + index;
            return (
              <div
                key={result.id}
                onClick={() => onSelect(result)}
                className={`px-4 py-2 cursor-pointer flex items-center gap-3 ${
                  actualIndex === selectedIndex
                    ? 'bg-info-light'
                    : 'hover:bg-muted'
                }`}
              >
                <span className="text-lg">{getTypeIcon(result.type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground truncate">
                    {result.title}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {result.documentNumber} • {getTypeLabel(result.type)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Project Documents Section */}
      {projectDocs.length > 0 && (
        <div className="border-b border-border">
          <div className="px-3 py-1.5 bg-muted text-xs font-medium text-muted-foreground uppercase tracking-wide">
            📁 Project Documents
          </div>
          {projectDocs.map((result, index) => {
            const actualIndex = openDocResults.length + recentDocs.length + index;
            return (
              <div
                key={result.id}
                onClick={() => onSelect(result)}
                className={`px-4 py-2 cursor-pointer flex items-center gap-3 ${
                  actualIndex === selectedIndex
                    ? 'bg-info-light'
                    : 'hover:bg-muted'
                }`}
              >
                <span className="text-lg">{getTypeIcon(result.type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-foreground truncate">
                    {result.title}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {result.documentNumber}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Search Results Section */}
      {query && (
        <div>
          <div className="px-3 py-1.5 bg-muted text-xs font-medium text-muted-foreground uppercase tracking-wide">
            🔍 Search Results {loading && '...'}
          </div>
          {results.length > 0 ? (
            results.map((result, index) => {
              const actualIndex = openDocResults.length + recentDocs.length + projectDocs.length + index;
              return (
                <div
                  key={result.id}
                  onClick={() => onSelect(result)}
                  className={`px-4 py-2 cursor-pointer flex items-center gap-3 ${
                    actualIndex === selectedIndex
                      ? 'bg-info-light'
                      : 'hover:bg-muted'
                  }`}
                >
                  <span className="text-lg">{getTypeIcon(result.type)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">
                      {result.title}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {result.documentNumber} • {getTypeLabel(result.type)}
                    </div>
                  </div>
                </div>
              );
            })
          ) : !loading && query.length > 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              No documents found for "{query}"
            </div>
          ) : null}
        </div>
      )}

      {/* Empty state */}
      {allResults.length === 0 && !loading && !query && (
        <div className="px-4 py-6 text-center text-muted-foreground">
          <p className="text-sm">No documents available</p>
          <p className="text-xs mt-1">Open a document first or type to search</p>
        </div>
      )}

      {/* Footer hint */}
      <div className="sticky bottom-0 px-3 py-2 bg-muted border-t border-border text-xs text-muted-foreground">
        <kbd className="px-1.5 py-0.5 bg-muted rounded">↑↓</kbd> Navigate
        <kbd className="px-1.5 py-0.5 bg-muted rounded ml-2">Enter</kbd> Select
        <kbd className="px-1.5 py-0.5 bg-muted rounded ml-2">Esc</kbd> Cancel
      </div>
    </div>
  );
}
